import React, { useState, useEffect } from "react";
import { Icons } from "../../constants/icon-library";
import { BeatLoader } from "react-spinners";
import { useNavigate } from "react-router-dom";
import { createSubscriptionSession } from "../../features/transaction/transactionAPI";
import { useDispatch, useSelector } from "react-redux";
import { editUser } from "../../features/user/userSlice";
import { getOrganizationByUuid } from "../../features/organization/organizationSlice";

const override = {
  display: "",
  margin: "0 auto",
  textAlign: "center",
  padding: "20px",
};

function Plans() {
  const dispatch = useDispatch();
  const [loadingPlan, setLoadingPlan] = useState(null);
  const { organization } = useSelector((state) => state.organization);
  const { user } = useSelector((state) => state.user);
  const { meetings, loading: meetingLoading } = useSelector(
    (state) => state.meetings,
  );

  const currentPlan = organization?.subscriptionPlan;
  const navigate = useNavigate();

  useEffect(() => {
    if (organization?.uuid) {
      dispatch(getOrganizationByUuid(organization.uuid));
    }
  }, [dispatch, organization?.uuid]);

  const onButtonClick = async (plan, priceId) => {
    setLoadingPlan(plan);
    const subscriptionSession = await createSubscriptionSession({
      subscriptionPlan: plan,
      priceId,
    });
    navigate(subscriptionSession.url);
    setLoadingPlan(null);
  };

  const handleCompleteOnboarding = async () => {
    try {
      await dispatch(
        editUser({
          uuid: user.uuid,
          data: {
            isOnboardingCompleted: true,
          },
        }),
      ).unwrap();

      navigate("/dashboard");
    } catch (err) {
      console.error("Failed to update onboarding status", err);
    }
  };

  const handleRedirect = async () => {
    if (meetingLoading) return;

    const hasMeetings = Array.isArray(meetings) && meetings.length > 0;

    navigate(hasMeetings ? "/onboarding/apps" : "/onboarding/apps");
  };

  return (
    <div className="container py-5 mt-4" style={{ maxWidth: "1400px" }}>
      <div className="card border-0 shadow-lg text-white">
        <div className="card-body p-4 p-md-5">
          <div className="row g-4">
            <div className="col-12 col-md-6 col-xl-4">
              <div className="card h-100 border-0 shadow pricing-card">
                <div className="card-body p-4 d-flex flex-column">
                  <div>
                    <h3 className="mb-2">Choose Your Plan</h3>
                    <p className="text-muted">
                      Select a plan to continue using all features and
                      integrations.
                    </p>
                  </div>

                  <div className="my-3">
                    <span
                      className="text-muted me-1"
                      style={{ fontSize: "1.5rem" }}
                    >
                      $
                    </span>
                    <span className="fw-bold" style={{ fontSize: "3rem" }}>
                      19
                    </span>
                  </div>

                  <ul className="list-unstyled mb-4">
                    <li className="mb-2 rounded-pill">✔ 10,000 Tokens</li>
                    <li className="mb-2 text-muted">
                      ✔ Up to 30 hours of conversation processing
                    </li>
                    <li className="mb-2 text-muted">
                      ✔ Up to 1,000 task identifications & creations
                    </li>
                    <li className="mb-2 text-muted">
                      ✔ Integration with all tools (Asana, Jira, Trello, Slack,
                      and more)
                    </li>
                  </ul>

                  <div className="mt-auto">
                    <button
                      onClick={() => {
                        if (currentPlan === "SUBSCRIPTION_1") return;
                        onButtonClick(
                          "SUBSCRIPTION_1",
                          process.env.REACT_APP_STRIPE_PERSONAL_PLAN,
                        );
                      }}
                      disabled={
                        loadingPlan === "SUBSCRIPTION_1" ||
                        currentPlan === "SUBSCRIPTION_1"
                      }
                      className="btn btn-outline-primary btn-lg w-100 text-nowrap"
                    >
                      <i className={`wb ${Icons("subscribe")} me-2`}></i>
                      {loadingPlan === "SUBSCRIPTION_1"
                        ? "Processing"
                        : currentPlan === "SUBSCRIPTION_1"
                          ? "Subscribed"
                          : "Subscribe"}
                      <BeatLoader
                        loading={loadingPlan === "SUBSCRIPTION_1"}
                        cssOverride={override}
                        color="var(--phoenix-emphasis-color)"
                        size="8px"
                      />
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <div className="col-12 col-md-6 col-xl-4">
              <div className="card h-100 border-primary shadow pricing-card">
                <div className="card-body p-4 d-flex flex-column">
                  <div>
                    <h3 className="mb-2 d-flex align-items-center gap-2">
                      Business Plan
                      <span
                        className="badge rounded-pill"
                        style={{ backgroundColor: "#162f4b" }}
                      >
                        #Popular
                      </span>
                    </h3>
                    <p className="text-muted">
                      Designed for growing teams with higher meeting volumes.
                    </p>
                  </div>

                  <div className="my-3">
                    <span
                      className="text-muted me-1"
                      style={{ fontSize: "1.5rem" }}
                    >
                      $
                    </span>
                    <span className="fw-bold" style={{ fontSize: "3rem" }}>
                      49
                    </span>
                  </div>

                  <ul className="list-unstyled mb-4">
                    <li className="mb-2 rounded-pill">✔ 30,000 Tokens</li>
                    <li className="mb-2">
                      ✔ Up to 90 hours of conversation processing
                    </li>
                    <li className="mb-2">
                      ✔ Up to 3,000 task identifications & creations
                    </li>
                    <li className="mb-2 text-muted">
                      ✔ Integration with all tools (Asana, Jira, Trello, Slack,
                      and more)
                    </li>
                  </ul>

                  <div className="mt-auto">
                    <button
                      onClick={() => {
                        if (currentPlan === "SUBSCRIPTION_2") return;
                        onButtonClick(
                          "SUBSCRIPTION_2",
                          process.env.REACT_APP_STRIPE_BUSINESS_PLAN,
                        );
                      }}
                      disabled={
                        loadingPlan === "SUBSCRIPTION_2" ||
                        currentPlan === "SUBSCRIPTION_2"
                      }
                      className="btn btn-primary btn-lg w-100 text-nowrap"
                    >
                      <i className={`wb ${Icons("subscribe")} me-2`}></i>
                      {loadingPlan === "SUBSCRIPTION_2"
                        ? "Processing"
                        : currentPlan === "SUBSCRIPTION_2"
                          ? "Subscribed"
                          : "Subscribe"}
                      <BeatLoader
                        loading={loadingPlan === "SUBSCRIPTION_2"}
                        cssOverride={override}
                        color="var(--phoenix-emphasis-color)"
                        size="8px"
                      />
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <div className="col-12 col-md-6 col-xl-4">
              <div className="card h-100 border-0 shadow pricing-card">
                <div className="card-body p-4 d-flex flex-column">
                  <div>
                    <h3 className="mb-2">Enterprise Plan</h3>
                    <p className="text-muted">
                      Built for organizations needing scalable automation.
                    </p>
                  </div>

                  <div className="my-3">
                    <span className="fw-bold" style={{ fontSize: "2.5rem" }}>
                      Custom
                    </span>
                  </div>

                  <ul className="list-unstyled mb-4">
                    <li className="mb-2 rounded-pill">
                      ✔ Custom token limits on usage
                    </li>
                    <li className="mb-2 text-muted">
                      ✔ Unlimited or high-volume conversation processing
                    </li>
                    <li className="mb-2 text-muted">
                      ✔ Advanced task extraction & bulk assignment capabilities
                    </li>
                    <li className="mb-2 text-muted">
                      ✔ All integrations + priority support + custom workflows
                    </li>
                  </ul>

                  <div className="mt-auto">
                    <button
                      className="btn btn-outline-primary btn-lg w-100 text-nowrap"
                      onClick={() =>
                        window.open("https://steertask.com/contact", "_blank")
                      }
                    >
                      <i className="wb webconnect-envelope me-2"></i>
                      Contact Us
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="d-flex justify-content-between align-items-center mt-5 pt-4 border-top">
        <button className="btn btn-primary px-4" onClick={handleRedirect}>
          ← Previous
        </button>

        <div className="d-flex align-items-center gap-3">
          <button
            className="btn btn-link text-muted"
            style={{ fontSize: "1.1rem" }}
            onClick={handleCompleteOnboarding}
          >
            Skip for now
          </button>

          <button
            className="btn btn-primary px-4"
            onClick={handleCompleteOnboarding}
          >
            Continue
          </button>
        </div>
      </div>
    </div>
  );
}

export default Plans;
